/**
 * ComplianceMemberTable - Data table for monthly contact compliance
 * With row selection checkboxes, notification status badges, and preferences action.
 */

import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  FileText,
  PauseCircle,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  BellOff,
  BellRing,
  ArrowUpToLine,
} from 'lucide-react';
import { ContactStatusBadge } from './ContactStatusBadge';
import type { MemberContactStatus } from '@/services/api/ecmReminders';
import { useFormattedDate } from '../../hooks/useUserTimezone';

interface ComplianceMemberTableProps {
  members: MemberContactStatus[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
  loading: boolean;
  onPageChange: (page: number) => void;
  onDocumentContact: (member: MemberContactStatus) => void;
  onSnoozeMember: (member: MemberContactStatus) => void;
  onViewEnrollment: (member: MemberContactStatus) => void;
  /** New: manage notification preferences for a member */
  onManageNotifications?: (member: MemberContactStatus) => void;
  /** New: selected member IDs for bulk actions */
  selectedMemberIds?: number[];
  /** New: toggle selection of a member */
  onToggleSelect?: (memberId: number) => void;
  /** New: toggle select all visible */
  onToggleSelectAll?: () => void;
}

export const ComplianceMemberTable: React.FC<ComplianceMemberTableProps> = ({
  members,
  total,
  page,
  pageSize,
  hasMore,
  loading,
  onPageChange,
  onDocumentContact,
  onSnoozeMember,
  onViewEnrollment,
  onManageNotifications,
  selectedMemberIds = [],
  onToggleSelect,
  onToggleSelectAll,
}) => {
  const { fmt } = useFormattedDate();

  const allVisibleSelected =
    members.length > 0 && members.every((m) => selectedMemberIds.includes(m.member_id));

  if (members.length === 0 && !loading) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <p className="text-gray-500 dark:text-gray-400">No members match the current filters.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-gray-50 dark:bg-gray-800/50">
                {/* Checkbox header */}
                {onToggleSelectAll && (
                  <th className="w-10 px-3 py-3">
                    <input
                      type="checkbox"
                      checked={allVisibleSelected}
                      onChange={onToggleSelectAll}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </th>
                )}
                <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-300">Member</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-300">Case #</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-300">Staff</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-300">Last Contact</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-300">Status</th>
                <th className="text-center px-4 py-3 font-medium text-gray-600 dark:text-gray-300">Days Left</th>
                <th className="text-center px-4 py-3 font-medium text-gray-600 dark:text-gray-300">Tier</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600 dark:text-gray-300">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {members.map((member) => (
                <tr
                  key={member.enrollment_id}
                  className={`hover:bg-gray-50 dark:hover:bg-gray-800/30 ${
                    member.contact_status === 'critical' ? 'bg-red-50/30 dark:bg-red-900/5' : ''
                  } ${selectedMemberIds.includes(member.member_id) ? 'bg-blue-50/40 dark:bg-blue-900/10' : ''}`}
                >
                  {/* Checkbox */}
                  {onToggleSelect && (
                    <td className="w-10 px-3 py-3">
                      <input
                        type="checkbox"
                        checked={selectedMemberIds.includes(member.member_id)}
                        onChange={() => onToggleSelect(member.member_id)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </td>
                  )}

                  {/* Member Name + notification status badges */}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        {member.member_name}
                      </span>
                      {member.is_snoozed && (
                        <Badge variant="outline" className="text-[10px] bg-gray-100 dark:bg-gray-700 gap-0.5 py-0 px-1.5">
                          <PauseCircle className="w-2.5 h-2.5" />
                          Snoozed
                        </Badge>
                      )}
                      {member.notifications_paused && !member.notifications_opted_out && (
                        <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-700 gap-0.5 py-0 px-1.5">
                          <BellOff className="w-2.5 h-2.5" />
                          Paused
                        </Badge>
                      )}
                      {member.notifications_opted_out && (
                        <Badge variant="outline" className="text-[10px] bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-700 gap-0.5 py-0 px-1.5">
                          <BellOff className="w-2.5 h-2.5" />
                          Opted Out
                        </Badge>
                      )}
                      {member.member_max_escalation_tier !== null &&
                        member.member_max_escalation_tier !== undefined &&
                        member.member_max_escalation_tier < 4 && (
                        <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-600 border-blue-200 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-700 gap-0.5 py-0 px-1.5">
                          <ArrowUpToLine className="w-2.5 h-2.5" />
                          {member.member_max_escalation_tier === 0
                            ? 'No Esc.'
                            : `Cap T${member.member_max_escalation_tier}`}
                        </Badge>
                      )}
                    </div>
                  </td>

                  {/* Case Number */}
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                    {member.case_number || '-'}
                  </td>

                  {/* Staff */}
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                    {member.staff_name || 'Unassigned'}
                  </td>

                  {/* Last Contact */}
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                    {member.last_contact_date
                      ? fmt.date(member.last_contact_date)
                      : <span className="text-red-500 text-xs font-medium">No contact</span>
                    }
                  </td>

                  {/* Status */}
                  <td className="px-4 py-3">
                    <ContactStatusBadge status={member.contact_status} size="sm" />
                  </td>

                  {/* Days Remaining */}
                  <td className="px-4 py-3 text-center">
                    <span className={`font-mono text-sm font-medium ${
                      member.days_remaining <= 3 ? 'text-red-600' :
                      member.days_remaining <= 7 ? 'text-orange-600' :
                      'text-gray-700 dark:text-gray-300'
                    }`}>
                      {member.days_remaining}
                    </span>
                  </td>

                  {/* Escalation Tier */}
                  <td className="px-4 py-3 text-center">
                    {member.highest_escalation_tier ? (
                      <Badge
                        variant="outline"
                        className={`text-xs ${
                          member.highest_escalation_tier >= 3
                            ? 'bg-red-100 text-red-700 border-red-200'
                            : member.highest_escalation_tier === 2
                            ? 'bg-orange-100 text-orange-700 border-orange-200'
                            : 'bg-gray-100 text-gray-700 border-gray-200'
                        }`}
                      >
                        <AlertTriangle className="w-3 h-3 mr-0.5" />
                        T{member.highest_escalation_tier}
                      </Badge>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>

                  {/* Actions */}
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {member.contact_status !== 'completed' && !member.is_snoozed && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 text-xs text-green-700 border-green-200 hover:bg-green-50"
                          onClick={() => onDocumentContact(member)}
                        >
                          <FileText className="w-3 h-3 mr-1" />
                          Document
                        </Button>
                      )}
                      {!member.is_snoozed && member.contact_status !== 'completed' && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 text-xs text-gray-600 border-gray-200 hover:bg-gray-50"
                          onClick={() => onSnoozeMember(member)}
                        >
                          <PauseCircle className="w-3 h-3 mr-1" />
                          Snooze
                        </Button>
                      )}
                      {onManageNotifications && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                          onClick={() => onManageNotifications(member)}
                          title="Notification preferences"
                        >
                          <BellRing className="w-3 h-3" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => onViewEnrollment(member)}
                      >
                        <ExternalLink className="w-3 h-3" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {total > pageSize && (
          <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50/50 dark:bg-gray-800/30">
            <span className="text-sm text-gray-500">
              Showing {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, total)} of {total}
            </span>
            <div className="flex gap-1">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => onPageChange(page - 1)}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={!hasMore}
                onClick={() => onPageChange(page + 1)}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
